import React, { Suspense } from 'react';
import type { DOMNode } from 'html-dom-parser';
import { domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import { Link } from '@core/js/components/common/Link';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';

import { parseHTMLAttributes, supportOnClickAttribute, alignAttributesWithReact } from '@core/js/utils/getHTMLParserOptions.util';

export { parseHTMLAttributes, supportOnClickAttribute, alignAttributesWithReact };

export const getHTMLParserOptions = <C extends object = any, L extends object = any>(
    { componentAttributes = {} as C, linkAttributes = {} as L, components, replacers = {} }: {
    componentAttributes?: C;
    linkAttributes?: L;
    components: Record<string, React.ComponentType<C>>;
    replacers?: Record<string, string>;
}): { options: HTMLReactParserOptions; scripts: HTMLScriptElement[] } => {
    const scripts: HTMLScriptElement[] = [];

    const options = {
        replace: (node: DOMNode): JSX.Element | undefined => {
            // #region @theme
            if (node.type === 'tag' && (node.name === 'iframe' || node.name === 'img')) {
                if (!node.attribs.loading) {
                    node.attribs.loading = 'lazy';
                }
            }
            // #endregion
            if (node.type === 'script') {
                const attribs = node.attribs || {};
                const script = document.createElement('script');

                script.appendChild(document.createTextNode(domToReact(node.children as DOMNode[]).toString()));

                for (const key of Object.keys(attribs)) {
                    script.setAttribute(key, attribs[key]);
                }

                scripts.push(script);

                // return empty react fragment so that nothing is rendered
                return <></>;
            }

            if (node.type === 'tag' && node.name === 'a') {
                // wrap all the links into Link element
                // so that regular routing can happen
                const attribs = node.attribs || {};

                return (
                    <Link
                        isDefaultLinkClass={false}
                        {...linkAttributes}
                        {...alignAttributesWithReact(attribs)}
                    >
                        {domToReact(node.children as DOMNode[], options)}
                    </Link>
                );
            }

            if (node.type === 'tag' && (node.name === 'head' || node.name === 'body')) {
                // do not support <head> and <body> dom elements, show their children instead
                return (
                    <>
                        {domToReact(node.children as DOMNode[], options)}
                    </>
                );
            }

            if (node.type === 'tag' && /^hub2-/.test(node.name)) {
                // name of the tag does stats with "hub2-"
                const attribs = node.attribs || {};
                const componentName = node.name?.replace(/^hub2-|-/g, '');

                const Component = components[node.name as keyof typeof components] ||
                                  components[componentName as keyof typeof components];

                if (Component) {
                    return (
                        <Suspense fallback="">
                            {node.children.length ? (
                                <Component
                                    {...componentAttributes}
                                    {...parseHTMLAttributes(attribs)}
                                >
                                    {domToReact(node.children as DOMNode[], options)}
                                </Component>
                            ) : (
                                <Component {...componentAttributes} {...parseHTMLAttributes(attribs)} />
                            )}
                        </Suspense>
                    );
                }

                // this is here only while we have Backbone replacers
                // it should be removed once all replacers are migrated to be real React components
                if (replacers?.[componentName]) {
                    return (
                        <BackboneReplacer dynamicName={componentName} viewName={componentName} {...node.attribs}>
                            {domToReact(node.children as DOMNode[], options)}
                        </BackboneReplacer>
                    );
                }
            }

            if (node.type === 'tag') {
                const attribs = node.attribs || {};
                const props = alignAttributesWithReact(attribs);
                const childrenContent = node.children?.length ? domToReact(node.children as DOMNode[], options) : null;

                if (attribs.class?.includes('fn-redirect') && (attribs['data-href'] || attribs['data-url']) && !attribs.tabindex) {
                    attribs.tabindex = '0';
                }

                if (Number(attribs.tabindex) === 0) {
                    return (
                        <AccessibilityElement tagName={node.name} {...props}>
                            {childrenContent}
                        </AccessibilityElement>
                    );
                }

                return React.createElement(node.name, props, childrenContent);
            }
        }
    };

    return { options, scripts };
};
